/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import Model, { helper, Providers } from "@tripian/model";
import {
  NumberCounter,
  DatePicker,
  Button,
  CustomPopover,
  Notification,
  SvgIcons,
} from "@tripian/react";
import classes from "./ViatorTourInfoForm.module.scss";

interface IViatorTourInfoForm {
  formPersonsCategories: Providers.Viator.AgeBand[];
  personsCategories: (Providers.Viator.AgeBand & { count: number })[];
  setPersonsCategories: (
    personsCategories: (Providers.Viator.AgeBand & { count: number })[]
  ) => void;
  bookableItems: Providers.Viator.BookableItem[];
  productCode: string;
  productOptionCode?: string;
  productURL: string;
  availableDate: string;
  setAvailableDate: (availableDate: string) => void;
  checkAvailabilityClick: (
    tourOptionsRequest: Providers.Viator.AvailabilityCheckRequest
  ) => Promise<boolean>;
  onScrollToAvailability?: () => void;
  bookingRequirements?: Providers.Viator.BookingRequirements;
  timeZone?: string;
  t: (value: Model.TranslationKey) => string;
}

const ViatorTourInfoForm: React.FC<IViatorTourInfoForm> = ({
  formPersonsCategories,
  personsCategories,
  setPersonsCategories,
  bookableItems,
  productCode,
  productOptionCode,
  productURL,
  availableDate,
  setAvailableDate,
  checkAvailabilityClick,
  onScrollToAvailability,
  bookingRequirements,
  timeZone = "UTC",
  t,
}) => {
  moment.locale(window.twindow.langCode);

  const [showPopOver, setShowPopOver] = useState<boolean>(false);
  const [showWarningMessage, setShowWarningMessage] = useState<boolean>(false);
  const [showAdultRequiredWarning, setShowAdultRequiredWarning] =
    useState<boolean>(false);
  const [prevPersonsCategories, setPrevPersonsCategories] = useState<
    (Providers.Viator.AgeBand & { count: number })[]
  >([]);
  const [isAvailabilityError, setIsAvailabilityError] =
    useState<boolean>(false);
  const [isCheckingAvailability, setIsCheckingAvailability] =
    useState<boolean>(false);

  const minimumDateTime = moment.tz(timeZone).add(1, "day");

  const isStateChanged = useMemo(() => {
    if (prevPersonsCategories.length !== personsCategories.length) {
      return true;
    }

    return personsCategories.some(currentItem => {
      const previousItem = prevPersonsCategories.find(
        item => item.ageBand === currentItem.ageBand
      );
      return !previousItem || previousItem.count !== currentItem.count;
    });
  }, [prevPersonsCategories, personsCategories]);

  const backdropClickHandler = (e: MouseEvent) => {
    e.stopPropagation();

    if (isStateChanged) {
      checkAvailabilityOnClick();
    }

    setShowPopOver(false);
  };

  const checkAvailabilityOnClick = async () => {
    const availabilityRequest: Providers.Viator.AvailabilityCheckRequest = {
      productCode,
      travelDate: availableDate || "",
      currency: "USD",
      paxMix: personsCategories
        .filter(item => item.count > 0)
        .map(item => ({
          ageBand: item.ageBand,
          numberOfTravelers: item.count,
        })),
    };

    const newPersonsCategories = helper.deepCopy(personsCategories);
    setPrevPersonsCategories(newPersonsCategories);

    setIsCheckingAvailability(true);
    const isAvailable = await checkAvailabilityClick(availabilityRequest);
    setIsCheckingAvailability(false);
    setIsAvailabilityError(!isAvailable);
  };

  const availableDates: string[] = useMemo(() => {
    const availableDatesSet = new Set<string>();

    bookableItems.forEach(bookableItem => {
      bookableItem.seasons.forEach(season => {
        const endDate = season.endDate
          ? moment.tz(season.endDate, timeZone)
          : moment.tz(timeZone).add(1, "year");
        let startDate: moment.Moment;

        if (moment.tz(season.startDate, timeZone).isBefore(minimumDateTime, "day")) {
          startDate = moment(minimumDateTime);
        } else if (endDate.isAfter(moment.tz(season.startDate, timeZone), "day")) {
          startDate = moment.tz(season.startDate, timeZone);
        } else {
          return;
        }

        const { pricingRecords } = season;

        const currentDate = moment(startDate);
        while (currentDate.isSameOrBefore(endDate, "day")) {
          if (
            !startDate ||
            currentDate.isSameOrAfter(moment(startDate), "day")
          ) {
            const dayOfWeek = currentDate
              .clone()
              .locale("en")
              .format("dddd")
              .toUpperCase(); // "MONDAY", "TUESDAY" vb.

            // daysOfWeek boş/tanımsızsa Viator bunu "her gün müsait" olarak kabul eder
            const matchingRecords = pricingRecords.filter(
              record =>
                !record.daysOfWeek ||
                record.daysOfWeek.length === 0 ||
                record.daysOfWeek.includes(dayOfWeek)
            );

            const dateStr = currentDate.format("YYYY-MM-DD");

            // Gün müsait sayılır: o günkü zaman dilimlerinden en az biri dolu değilse
            const hasAvailableTimeSlot = matchingRecords.some(record => {
              const timedEntries = record.timedEntries ?? [];
              if (timedEntries.length === 0) {
                return true;
              }
              return timedEntries.some(
                entry =>
                  !entry.unavailableDates ||
                  !entry.unavailableDates.some(
                    dateEntry => dateEntry.date === dateStr
                  )
              );
            });

            if (hasAvailableTimeSlot) {
              availableDatesSet.add(dateStr);
            }
          }
          currentDate.add(1, "day");
        }
      });
    });

    return Array.from(availableDatesSet).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );
  }, [bookableItems, minimumDateTime, timeZone]);

  const isButtonDisabled = useMemo(() => {
    const totalTravelers = personsCategories.reduce(
      (accumulator, currentValue) => accumulator + currentValue.count,
      0
    );

    const hasAdult = personsCategories.some(
      category =>
        (category.ageBand === "ADULT" || category.ageBand === "TRAVELER") &&
        category.count > 0
    );

    // Check if all counts meet their respective minTravelersPerBooking requirement
    const meetsMinTravelersPerBooking = personsCategories.every(
      category => category.count >= (category.minTravelersPerBooking || 0)
    );

    return !!(
      !availableDate ||
      (bookingRequirements?.maxTravelersPerBooking !== undefined &&
        totalTravelers > bookingRequirements.maxTravelersPerBooking) ||
      (bookingRequirements?.minTravelersPerBooking !== undefined &&
        totalTravelers < bookingRequirements.minTravelersPerBooking) ||
      (bookingRequirements?.requiresAdultForBooking && !hasAdult) || // Adult required but not present
      !meetsMinTravelersPerBooking
    );
  }, [availableDate, bookingRequirements, personsCategories]);

  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (!isButtonDisabled && !hasAutoTriggered.current) {
      hasAutoTriggered.current = true;
      checkAvailabilityOnClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isButtonDisabled]);

  // if (window.tconfig.PROVIDERS.tourAndTicket.find((t) => t.id === Model.PROVIDER_ID.VIATOR)?.prod === true) {
  //   return (
  //     <div className={classes.mainDiv}>
  //       <div className="center">
  //         <Button color="primary" className={classes.addToChartButton} onClick={() => window.open(productURL)} text={t("trips.myTrips.localExperiences.tourDetails.bookNow")} />
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <div className="bg-white rounded-2xl shadow-lg p-6 w-full">
      {bookableItems.length > 0 &&
        (availableDates.length === 0 || isAvailabilityError) ? (
        <div className="row mb0 bg-white py-4 rounded-2xl flex items-center justify-center shadow-lg text-primary-color">
          <span className={classes.warningMessage}>
            {t("trips.myTrips.localExperiences.tourDetails.noAvailableDays")}
          </span>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4 mb-4">
            <div className={classes.viatorTourInfoFormDatePickerWrapper}>
              {availableDate && availableDates.length > 0 && (
                <DatePicker
                  currentDate={moment(availableDate)}
                  startDate={moment(availableDates[0])}
                  endDate={moment(availableDates[availableDates.length - 1])}
                  onchanged={date => {
                    if (date) {
                      const selectedDate = date.format("YYYY-MM-DD");
                      setAvailableDate(selectedDate);
                    }
                  }}
                />
              )}
            </div>
            <div>
              <CustomPopover
                positions={["bottom"]}
                show={showPopOver}
                backdropClick={backdropClickHandler}
                content={
                  <div className={classes.gygTourInfoListItemWrapper}>
                    {[...formPersonsCategories].reverse().map((formPersonsCategory, i) => {
                      const count: number =
                        personsCategories.find(
                          x => x.ageBand === formPersonsCategory.ageBand
                        )?.count ?? 0;
                      return (
                        <li
                          key={`${formPersonsCategory.ageBand}-${i}`}
                          className={classes.gygTourInfoFormListItem}
                        >
                          <div>
                            <h4 className={classes.gygTourInfoPersonCountText}>
                              <div>
                                {formPersonsCategory.ageBand} (
                                {formPersonsCategory.startAge} -{" "}
                                {formPersonsCategory.endAge})
                              </div>
                            </h4>
                            <div className={classes.gygTourInfoPersonAge}>
                              (
                              {t(
                                "trips.myTrips.localExperiences.tourDetails.minimum"
                              )}
                              : {formPersonsCategory.minTravelersPerBooking},{" "}
                              {t(
                                "trips.myTrips.localExperiences.tourDetails.max"
                              )}
                              : {formPersonsCategory.maxTravelersPerBooking})
                            </div>
                          </div>
                          <div className={classes.gygTourInfoPersonCount}>
                            {personsCategories.length > 0 && (
                              <NumberCounter
                                defaultValue={count}
                                minValue={0}
                                maxValue={100}
                                iconFill="blue"
                                onchange={newCount => {
                                  const newPersonsCategories = [
                                    ...personsCategories,
                                  ];
                                  const findX = newPersonsCategories.findIndex(
                                    x =>
                                      x.ageBand === formPersonsCategory.ageBand
                                  );
                                  if (findX > -1) {
                                    newPersonsCategories[findX].count =
                                      newCount;
                                    // Check if total travelers exceed the maximum limit
                                    const totalTravelers =
                                      newPersonsCategories.reduce(
                                        (accumulator, currentValue) =>
                                          accumulator + currentValue.count,
                                        0
                                      );
                                    if (
                                      bookingRequirements?.maxTravelersPerBooking &&
                                      totalTravelers >
                                      bookingRequirements.maxTravelersPerBooking
                                    ) {
                                      setShowWarningMessage(true);
                                    } else {
                                      setShowWarningMessage(false);
                                    }
                                    // Check if adult is required and none is present
                                    const hasAdult = newPersonsCategories.some(
                                      category =>
                                        (category.ageBand === "ADULT" ||
                                          category.ageBand === "TRAVELER") &&
                                        category.count > 0
                                    );
                                    if (
                                      bookingRequirements?.requiresAdultForBooking &&
                                      !hasAdult
                                    ) {
                                      setShowAdultRequiredWarning(true);
                                    } else {
                                      setShowAdultRequiredWarning(false);
                                    }
                                    setPersonsCategories(newPersonsCategories);
                                  }
                                }}
                              />
                            )}
                          </div>
                        </li>
                      );
                    })}
                    <h3 className={classes.viatorTourInfoFormMaxWarning}>
                      ({" "}
                      {t("trips.myTrips.localExperiences.tourDetails.minimum")}{" "}
                      {bookingRequirements?.minTravelersPerBooking} - &nbsp;
                      {t("trips.myTrips.localExperiences.tourDetails.max")}{" "}
                      {bookingRequirements?.maxTravelersPerBooking}{" "}
                      {t("trips.myTrips.localExperiences.tourDetails.traveler")}{" "}
                      )
                    </h3>
                    <Button
                      disabled={isButtonDisabled}
                      className="w-full mt-4"
                      onClick={() => {
                        checkAvailabilityOnClick();
                        setShowPopOver(false);
                      }}
                      text={t(
                        "trips.myTrips.itinerary.step.addToItinerary.submit.apply"
                      )}
                    />
                  </div>
                }
              >
                <div
                  onClick={() => {
                    setShowPopOver(!showPopOver);
                  }}
                  role="button"
                  onKeyDown={() => { }}
                  tabIndex={0}
                  className={classes.gygTourInfoPeopleButton}
                  key="viator-tour-info"
                >
                  <SvgIcons.Avatar />
                  {personsCategories
                    .filter(p => p.count !== 0)
                    .map((personsCategory, index, self) => {
                      const comma = index + 1 !== self.length ? "," : "";
                      if (
                        personsCategory.ageBand === "CHILD" &&
                        personsCategory.count > 1
                      ) {
                        return (
                          <span
                            key={`${personsCategory.ageBand}-${personsCategory.count}-${index}-c`}
                          >
                            {personsCategory.count}{" "}
                            {t("trips.toursAndTickets.children.label")}
                            {comma}
                          </span>
                        );
                      } else if (personsCategory.count > 1) {
                        return (
                          <span
                            key={`${personsCategory.ageBand}-${personsCategory.count}-${index}-i`}
                          >
                            {personsCategory.count}{" "}
                            {personsCategory.ageBand[0] +
                              personsCategory.ageBand.toLowerCase().slice(1)}
                            s{comma}
                          </span>
                        );
                      }
                      return (
                        <span
                          key={`${personsCategory.ageBand}-${personsCategory.count}-${index}-a`}
                        >
                          {personsCategory.count}{" "}
                          {personsCategory.ageBand[0] +
                            personsCategory.ageBand.toLowerCase().slice(1)}
                          {comma}
                        </span>
                      );
                    })}
                </div>
              </CustomPopover>
            </div>
          </div>
          <Button
            disabled={isButtonDisabled || isCheckingAvailability}
            className="w-full mt-4 text-base lg:text-lg py-3"
            onClick={() => {
              onScrollToAvailability?.();
              checkAvailabilityOnClick();
            }}
            color="primary"
            text={isCheckingAvailability ? "..." : t("trips.myTrips.localExperiences.tourDetails.bookNow")}
          />
          {showWarningMessage ? (
            <Notification
              type="warning"
              positionX="center"
              positionY="top"
              title={t(
                "trips.myTrips.localExperiences.tourDetails.maxTravelerAmountReached"
              )}
              message={t(
                "trips.myTrips.localExperiences.tourDetails.pleaseLowerTheTravelerCount"
              )}
              onClose={() => {
                setShowWarningMessage(false);
              }}
              closeMs={3500}
            />
          ) : null}
          {showAdultRequiredWarning && (
            <Notification
              type="warning"
              positionX="center"
              positionY="top"
              title={t(
                "trips.myTrips.localExperiences.tourDetails.adultRequired"
              )}
              message={t(
                "trips.myTrips.localExperiences.tourDetails.pleaseAddAtLeastOneAdult"
              )}
              onClose={() => {
                setShowAdultRequiredWarning(false);
              }}
              closeMs={35000}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ViatorTourInfoForm;
